import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Autocomplete, TableCell, TableRow, Checkbox, Tooltip,
    Button, Chip, TextField,
    FormControlLabel, Snackbar, Alert,
    FormControl, Select, MenuItem, IconButton, ListItemText, OutlinedInput
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import * as API from '../../api';
import DataTable from '../DataTable';
import { formatDateDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import CustomDatePicker from '../CustomDatePicker';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefundInfoDialog from './RefundInfoDialog';
import PaymentIcon from '@mui/icons-material/Payment';
import BulkPaymentDialog from '../BulkPaymentDialog';
import ConfirmDialog from '../ConfirmDialog';
import ReportExportDialog from './ReportExportDialog'; // Thêm dòng này

const BOOKING_STATUS_COLORS = { 'PENDING': 'warning', 'CONFIRMED': 'success', 'CANCELLED': 'error', 'LOST': 'default', 'CLOSED': 'info' };
const BOOKING_STATUS_LABELS = { 'PENDING': 'Chờ xử lý', 'CONFIRMED': 'Đã xác nhận', 'CANCELLED': 'Đã hủy', 'LOST': 'Deal hỏng', 'CLOSED': 'Hoàn thành' };
const OPS_STATUS_COLORS = { 'OPEN': 'error', 'CONTACTED': 'warning', 'CONFIRMED': 'info', 'OPERATING': 'secondary', 'DONE': 'success' };
const OPS_STATUS_LABELS = { 'OPEN': 'Chờ ĐH', 'CONTACTED': 'Đã liên hệ', 'CONFIRMED': 'NCC Xác nhận', 'OPERATING': 'Đang đi', 'DONE': 'Đã đi' };
const LIABILITY_STATUS_COLORS = { 'UNPAID': 'error', 'PARTIAL': 'warning', 'PAID': 'success' };
const LIABILITY_STATUS_LABELS = { 'UNPAID': 'Chưa TT', 'PARTIAL': 'TT 1 phần', 'PAID': 'Đã TT hết' };

const LIABILITY_FILTER_OPTIONS = { 
    'DEBT_TOUR': 'Còn nợ Tour', 
    'DEBT_TRANSPORT': 'Còn nợ Xe', 
    'DEBT_ROOM': 'Còn nợ Phòng', 
    'DEBT_EXTRA': 'Còn nợ Khác/DV', // <--- THÊM MỚI
    'PAID': 'Trả hết nợ' 
};
const SOURCE_OPTIONS = ['Website', 'Facebook', 'Zalo', 'Klook', 'Viator', 'Direct'];
const BOOKING_TYPE_OPTIONS = { 'DIRECT': 'Trực tiếp ĐH', 'INTERMEDIARY': 'Trung gian' };
const BOOKING_CATEGORY_OPTIONS = {
    'TOUR': 'Tour trọn gói/Ghép',
    'TRANSPORT': 'Dịch vụ Thuê xe',
    'HOTEL': 'Dịch vụ Phòng KS',
    'FLIGHT': 'Vé máy bay',
    'COMBO': 'Combo Dịch vụ',
    'OTHER': 'Dịch vụ Khác'
};

const ALL_COLUMNS_DEF = [
    { field: 'booking_code', label: 'Mã BK', minWidth: 90 },
    { field: 'source', label: 'Nguồn BK', minWidth: 100, align: 'center' },
    { field: 'booking_type', label: 'Loại BK', minWidth: 110, align: 'center' },
    { field: 'intermediary_note', label: 'Thỏa thuận', minWidth: 150 },
    { field: 'customer', label: 'Khách hàng', minWidth: 150 },
    { field: 'tour_name', label: 'Tour / Dịch vụ', minWidth: 180 },
    { field: 'start_date', label: 'Ngày đi', minWidth: 100 },
    { field: 'end_date', label: 'Ngày về', minWidth: 100 },
    { field: 'sales_owner', label: 'NV Phụ trách', minWidth: 130 },
    { field: 'total_price', label: 'Tổng thu', minWidth: 120, align: 'right' },
    { field: 'total_liability', label: 'Tổng CN', minWidth: 120, align: 'right' },
    { field: 'total_remaining_liability', label: 'CN Còn lại', minWidth: 120, align: 'right' },
    { field: 'profit', label: 'Lợi nhuận', minWidth: 120, align: 'right' },
    { field: 'profit_margin', label: '% LN', minWidth: 80, align: 'center' },
    { field: 'payment_status', label: 'Thu khách', minWidth: 110, align: 'center' },
    { field: 'liability_status', label: 'TT Công nợ', minWidth: 110, align: 'center' },
    { field: 'booking_status', label: 'TT Booking', minWidth: 120 },
    { field: 'operation_status', label: 'TT Điều hành', minWidth: 120 },
    { field: 'refund_amount', label: 'Số tiền cần hoàn', minWidth: 120, align: 'right' },
    { field: 'refund_account_info', label: 'Thông tin TK hoàn', minWidth: 200 },
];

const DEFAULT_VISIBLE_COLUMNS = [
    'booking_code', 
    'start_date', 
    'customer',
    'total_price', 
    'total_liability',
    'profit', 
    'sales_owner', 
    'operation_status',
    'booking_status', 
    'liability_status'
];

const ACTION_COLUMN = { field: 'action', label: '', minWidth: 80, align: 'center' };

const DEFAULT_OPS_STATUS = Object.keys(OPS_STATUS_LABELS);

// --- SỬA 2: Thêm DEBT_EXTRA vào bộ lọc mặc định ---
const DEFAULT_LIABILITY_FILTER = ['DEBT_TOUR', 'DEBT_TRANSPORT', 'DEBT_ROOM', 'DEBT_EXTRA'];

const DEFAULTS = {
    bookingStatus: ['PENDING', 'CONFIRMED', 'CLOSED'],
    opsStatus: Object.keys(OPS_STATUS_LABELS),
    liability: ['DEBT_TOUR', 'DEBT_TRANSPORT', 'DEBT_ROOM', 'DEBT_EXTRA'],
    bookingType: ['DIRECT', 'INTERMEDIARY'],
    category: Object.keys(BOOKING_CATEGORY_OPTIONS), // <-- Thêm mới
    sort: 'asc',
    quickFilter: 'CUSTOM'
};

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const BookingListTab = forwardRef((props, ref) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const getParam = (key, defaultVal) => {
        const val = searchParams.get(key);
        if (!val) return defaultVal;
        if (Array.isArray(defaultVal)) return val.split(',');
        return val;
    };

    const [bookings, setBookings] = useState([]);
    const [tours, setTours] = useState([]);
    const [salesOwners, setSalesOwners] = useState([]);
    const [suppliers, setSuppliers] = useState([]); // <-- Thêm mới
    const [filterSupplier, setFilterSupplier] = useState(null); // <-- Thêm mới
    const [filterCategory, setFilterCategory] = useState(getParam('category', DEFAULTS.category)); // <-- Thêm mới
    const [reportOpen, setReportOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState(searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [filterEndDate, setFilterEndDate] = useState(searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [isAllTime, setIsAllTime] = useState(searchParams.get('all_time') === 'true');
    const [filterBookingStatus, setFilterBookingStatus] = useState(getParam('bk_status', DEFAULTS.bookingStatus));
    const [filterOpsStatus, setFilterOpsStatus] = useState(getParam('ops_status', DEFAULTS.opsStatus));
    const [filterLiability, setFilterLiability] = useState(getParam('liability', DEFAULTS.liability));
    const [filterSource, setFilterSource] = useState(SOURCE_OPTIONS);
    const [filterBookingType, setFilterBookingType] = useState(getParam('bk_type', DEFAULTS.bookingType));
    const [filterTour, setFilterTour] = useState(null);
    const [filterSaleOwner, setFilterSaleOwner] = useState(null);
    const [quickFilterMode, setQuickFilterMode] = useState(getParam('mode', DEFAULTS.quickFilter));
    const [sortOrder, setSortOrder] = useState(getParam('sort', DEFAULTS.sort));
    const [selectedBookings, setSelectedBookings] = useState([]);
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [bulkPayOpen, setBulkPayOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ 
        open: false, 
        title: '', 
        content: '', 
        onConfirm: null 
    });
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const [activeFilters, setActiveFilters] = useState({
        startDate: searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        bookingStatus: getParam('bk_status', DEFAULTS.bookingStatus),
        opsStatus: getParam('ops_status', DEFAULTS.opsStatus),
        liability: getParam('liability', DEFAULTS.liability),
        source: SOURCE_OPTIONS,
        allTime: searchParams.get('all_time') === 'true',
        bookingType: getParam('bk_type', DEFAULTS.bookingType),
        sort: searchParams.get('sort') || 'asc',
        tour: null,
        saleOwner: null,
        supplier: null, // <-- Thêm mới
        category: getParam('category', DEFAULTS.category) // <-- Thêm mới
    });

    useImperativeHandle(ref, () => ({
        resetFilters: () => {
            const defaultStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
            const defaultEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

            setFilterStartDate(defaultStart);
            setFilterEndDate(defaultEnd);
            setIsAllTime(false);
            setFilterSaleOwner({ id: 'ALL', first_name: '', last_name: 'Tất cả' });
            setFilterTour(null);
            setSortOrder(DEFAULTS.sort);
            setQuickFilterMode(DEFAULTS.quickFilter);
            setFilterSource(SOURCE_OPTIONS);
            setFilterBookingStatus(DEFAULTS.bookingStatus);
            setFilterOpsStatus(DEFAULTS.opsStatus);
            setFilterLiability(DEFAULTS.liability);
            setFilterBookingType(DEFAULTS.bookingType);

            setSearchParams({ tab: 'BOOKING' });

            setActiveFilters({
                startDate: defaultStart,
                endDate: defaultEnd,
                bookingStatus: DEFAULTS.bookingStatus,
                opsStatus: DEFAULTS.opsStatus,
                liability: DEFAULTS.liability,
                source: SOURCE_OPTIONS,
                allTime: false,
                sort: DEFAULTS.sort,
                tour: null,
                saleOwner: { id: 'ALL' }
            });

            setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
            setSelectedBookings([]);

            loadData();
        },
        openExportDialog: () => setReportOpen(true)
    }));

    const syncToUrl = () => {
        const params = {};
        if (isAllTime) params.all_time = 'true';
        else { params.start = filterStartDate; params.end = filterEndDate; }

        if (quickFilterMode !== DEFAULTS.quickFilter) params.mode = quickFilterMode;
        if (sortOrder !== DEFAULTS.sort) params.sort = sortOrder;
        if (filterBookingStatus.length !== DEFAULTS.bookingStatus.length) params.bk_status = filterBookingStatus.join(',');
        if (filterOpsStatus.length !== DEFAULTS.opsStatus.length) params.ops_status = filterOpsStatus.join(',');
        if (filterLiability.length !== DEFAULTS.liability.length) params.liability = filterLiability.join(',');
        if (filterBookingType.length !== DEFAULTS.bookingType.length) params.bk_type = filterBookingType.join(',');

        const currentTab = searchParams.get('tab');
        if (currentTab) params.tab = currentTab;
        if (filterTour) params.tour = filterTour.value;
        if (filterSaleOwner && filterSaleOwner.id !== 'ALL') params.sale_owner = filterSaleOwner.id;
        if (filterCategory.length !== DEFAULTS.category.length) params.category = filterCategory.join(',');
        if (filterSupplier && filterSupplier.id !== 'ALL') params.supplier = filterSupplier.id;

        setSearchParams(params);
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [bkRes, tourRes, userRes,supRes] = await Promise.all([
                API.getAllBookings(), 
                API.fetchTours(),
                API.fetchUsers(),
                API.fetchAllServiceProviders()
            ]);
            setBookings(bkRes.data);
            setTours(tourRes.data);
            setSuppliers([{id: 'ALL', name: 'Tất cả NCC'}, ...supRes.data]);
            
            const allOption = { id: 'ALL', first_name: '', last_name: 'Tất cả' };
            const users = userRes.data || [];
            const validUsers = users.filter(u => u.id !== 1 && `${u.last_name || ''} ${u.first_name || ''}`.trim().length > 0);
            const salesOwnerList = [allOption, ...validUsers];
            setSalesOwners(salesOwnerList);

            const urlTourValue = searchParams.get('tour');
            const urlSaleOwnerId = searchParams.get('sale_owner');

            let foundTour = null;
            if (urlTourValue) {
                const baseNames = [...new Set(tourRes.data.map(t => t.base_tour_name))].filter(Boolean);
                const opts = baseNames.map(base => ({ label: base, value: base }));
                foundTour = opts.find(opt => opt.value === urlTourValue);
                if (foundTour) setFilterTour(foundTour);
            }

            let foundOwner = allOption;
            if (urlSaleOwnerId) {
                foundOwner = salesOwnerList.find(s => s.id.toString() === urlSaleOwnerId) || allOption;
                setFilterSaleOwner(foundOwner);
            } else {
                setFilterSaleOwner(allOption);
            }

            setActiveFilters(prev => ({
                ...prev,
                tour: foundTour,
                saleOwner: foundOwner
            }));

        } catch (err) {
            console.error("Lỗi tải dữ liệu:", err);
        }
    };

    const calculateFinancials = (bk) => {
        const revenueDisplay = parseFloat(bk.total_price) || 0;
        
        const payables = bk.payables || [];
        const refundPayable = payables.find(p => p.payable_type === 'REFUND');
        const refundAmount = refundPayable ? parseFloat(refundPayable.amount) : 0;

        let realRevenue = revenueDisplay;
        if (bk.booking_status === 'CANCELLED') {
            const received = parseFloat(bk.amount_received) || 0;
            realRevenue = received - refundAmount;
        }

        const totalLiability = payables
            .filter(p => p.payable_type !== 'REFUND' || (p.payable_type === 'REFUND' && p.supplier)) 
            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        
        const remainingLiability = payables
            .filter(p => p.payable_type !== 'REFUND' || (p.payable_type === 'REFUND' && p.supplier))
            .reduce((sum, p) => p.status !== 'PAID' ? sum + (parseFloat(p.amount) || 0) : sum, 0);
        
        const profit = realRevenue - totalLiability;

        const margin = realRevenue > 0 ? (profit / realRevenue) * 100 : 0;
        
        return { 
            revenue: revenueDisplay,
            totalLiability, 
            remainingLiability, 
            profit, 
            margin,
            refund_amount: refundAmount,
            refund_account_info: refundPayable ? refundPayable.receiver_info : ''
        };
    };

    const bulkPayStatus = useMemo(() => {
        if (selectedBookings.length === 0) return { isSameSource: false, hasPaidItems: false };
        
        const firstSource = selectedBookings[0].source || 'Direct';
        const isSameSource = selectedBookings.every(b => (b.source || 'Direct') === firstSource);

        const hasPaidItems = selectedBookings.some(b => {
            const total = parseFloat(b.total_price) || 0;
            const received = parseFloat(b.amount_received) || 0;
            const remain = total - received;
            return remain <= 0 || b.payment_status === 'PAID';
        });

        return { isSameSource, hasPaidItems };
    }, [selectedBookings]);

    const totalSelectedValue = useMemo(() => {
        return selectedBookings.reduce((sum, b) => sum + (parseFloat(b.total_price) || 0), 0);
    }, [selectedBookings]);

    const getTourFilterOptions = () => {
        const baseNames = [...new Set(tours.map(t => t.base_tour_name))].filter(Boolean);
        return baseNames.map(base => ({ label: base, value: base }));
    };

    const filteredList = useMemo(() => {
        return bookings.filter(bk => {
            if (quickFilterMode === 'REFUND_PENDING') {
                const payables = bk.payables || [];
                
                const hasUnpaidRefund = payables.some(p => 
                    p.payable_type === 'REFUND' && 
                    p.status !== 'PAID' && 
                    parseFloat(p.amount) > 0
                );
                
                if (hasUnpaidRefund) {
                    return true;
                }

                const legacyRefundAmount = parseFloat(bk.refund_amount || 0);
                if (legacyRefundAmount > 0 && bk.refund_status !== 'COMPLETED') {
                    return true;
                }

                return false; 
            }
            if (quickFilterMode === 'COLLECTION_PENDING') {
                const payments = bk.payments || [];
                const hasUnsettledCollection = payments.some(p => 
                    p.payment_type === 'COLLECTION' && 
                    p.status !== 'SETTLED' && 
                    p.status !== 'REJECTED'
                );
                return hasUnsettledCollection;
            }
            if (!activeFilters.allTime) {
                if (!bk.start_date) return false;
                const bkDateStr = bk.start_date.split('T')[0];
                if (bkDateStr < activeFilters.startDate || bkDateStr > activeFilters.endDate) return false;
            }
            if (activeFilters.bookingStatus.length > 0 && !activeFilters.bookingStatus.includes(bk.booking_status)) return false;
            if (activeFilters.opsStatus.length > 0) {
                const opsStt = bk.operation_status || 'OPEN';
                if (!activeFilters.opsStatus.includes(opsStt)) return false;
            }
            if (activeFilters.source && activeFilters.source.length > 0) {
                 const bkSource = bk.source || 'Direct'; 
                 if (!activeFilters.source.includes(bkSource)) return false;
            }
            if (activeFilters.bookingType && activeFilters.bookingType.length > 0) {
                const bkType = bk.booking_type || 'DIRECT';
                if (!activeFilters.bookingType.includes(bkType)) return false;
            }
            // 1. Lọc theo Booking Category
            if (activeFilters.category && activeFilters.category.length > 0) {
                const bCat = bk.booking_category || 'TOUR'; // Mặc định là TOUR nếu cũ
                if (!activeFilters.category.includes(bCat)) return false;
            }

            // 2. Lọc theo Nhà cung cấp
            if (activeFilters.supplier && activeFilters.supplier.id !== 'ALL') {
                const supId = activeFilters.supplier.id;
                let relatesToSupplier = false;

                // Kiểm tra NCC của Tour
                if (bk.tour) {
                    const bkTour = tours.find(t => t.tour_code === bk.tour);
                    if (bkTour && bkTour.supplier === supId) relatesToSupplier = true;
                }
                
                // Kiểm tra NCC của Xe, Phòng, Dịch vụ phát sinh, Công nợ
                if (!relatesToSupplier && bk.transports?.some(t => t.supplier === supId)) relatesToSupplier = true;
                if (!relatesToSupplier && bk.rooms?.some(r => r.supplier === supId)) relatesToSupplier = true;
                if (!relatesToSupplier && bk.extras?.some(e => e.supplier === supId)) relatesToSupplier = true;
                if (!relatesToSupplier && bk.payables?.some(p => p.supplier === supId)) relatesToSupplier = true;

                if (!relatesToSupplier) return false;
            }
            if (activeFilters.saleOwner && activeFilters.saleOwner.id !== 'ALL') {
                if (bk.sale_owner_id !== activeFilters.saleOwner.id) return false;
            }
            if (activeFilters.liability.length > 0) {
                const payables = bk.payables || [];
                let matches = false;
                if (activeFilters.liability.includes('DEBT_TOUR') && payables.some(p => p.payable_type === 'TOUR' && p.status !== 'PAID')) matches = true;
                if (!matches && activeFilters.liability.includes('DEBT_TRANSPORT') && payables.some(p => p.payable_type.includes('TRANSPORT') && p.status !== 'PAID')) matches = true;
                if (!matches && activeFilters.liability.includes('DEBT_ROOM') && payables.some(p => p.payable_type === 'ROOM' && p.status !== 'PAID')) matches = true;
                
                if (!matches && activeFilters.liability.includes('DEBT_EXTRA') && payables.some(p => 
                    (p.payable_type === 'EXTRA' || p.payable_type === 'OTHER' || (p.payable_type === 'REFUND' && p.supplier)) 
                    && p.status !== 'PAID'
                )) matches = true;

                if (!matches && activeFilters.liability.includes('PAID') && (bk.liability_status === 'PAID' || payables.length === 0)) matches = true;
                if (!matches && bk.booking_status === 'CANCELLED') matches = true; 
                if (!matches) return false;
            }
            if (activeFilters.tour) {
                const bookingTour = tours.find(t => t.tour_code === bk.tour);
                if (!bookingTour || bookingTour.base_tour_name !== activeFilters.tour.value) return false;
            }
            if (quickFilterMode === 'READY_TO_CLOSE') {
                if (bk.booking_status === 'CLOSED' || bk.booking_status === 'CANCELLED') return false;
                const isLiabilityPaid = bk.liability_status === 'PAID' || (bk.payables || []).length === 0;
                if (bk.operation_status !== 'DONE' || bk.payment_status !== 'PAID' || !isLiabilityPaid) return false;
            } else if (quickFilterMode === 'DEBT_CUSTOMER') {
                if (bk.payment_status === 'PAID') return false;
            }
            return true;
        }).sort((a, b) => {
            const timeA = a.start_date ? new Date(a.start_date).getTime() : 0;
            const timeB = b.start_date ? new Date(b.start_date).getTime() : 0;
            return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
        });
    }, [bookings, activeFilters, tours, quickFilterMode, sortOrder]);

    const summaryData = useMemo(() => {
        const stats = filteredList.reduce((acc, bk) => {
            const { revenue, totalLiability, remainingLiability, profit } = calculateFinancials(bk);
            acc.count += 1; acc.totalRevenue += revenue; acc.totalLiability += totalLiability;
            acc.totalRemainingLiability += remainingLiability; acc.totalProfit += profit;
            return acc;
        }, { count: 0, totalRevenue: 0, totalLiability: 0, totalRemainingLiability: 0, totalProfit: 0 });

        return {
            stt: '', booking_code: `SL: ${stats.count}`, 
            total_price: formatCurrency(stats.totalRevenue),
            total_liability: formatCurrency(stats.totalLiability),
            total_remaining_liability: formatCurrency(stats.totalRemainingLiability),
            profit: formatCurrency(stats.totalProfit), action: '' 
        };
    }, [filteredList]);

    const handleApplyFilter = () => {
        setSelectedBookings([]);
        syncToUrl();
        setActiveFilters({
            startDate: filterStartDate, endDate: filterEndDate,
            bookingStatus: filterBookingStatus, opsStatus: filterOpsStatus,
            liability: filterLiability, source: filterSource, bookingType: filterBookingType,
            allTime: isAllTime, sort: sortOrder, tour: filterTour, saleOwner: filterSaleOwner,
            category: filterCategory, supplier: filterSupplier
        });
        setVisibleColumns(prev => {
            let next = [...prev];
            const addCol = (col) => { if (!next.includes(col)) next.push(col); };
            if (filterSource.length !== SOURCE_OPTIONS.length) addCol('source');
            const isOpsDefault = filterOpsStatus.length === DEFAULT_OPS_STATUS.length && filterOpsStatus.every(val => DEFAULT_OPS_STATUS.includes(val));
            if (!isOpsDefault) { addCol('operation_status'); addCol('end_date'); }
            if (filterSaleOwner && filterSaleOwner.id !== 'ALL') addCol('sales_owner');
            const isLiabilityDefault = filterLiability.length === DEFAULT_LIABILITY_FILTER.length && filterLiability.every(val => DEFAULT_LIABILITY_FILTER.includes(val));
            if (!isLiabilityDefault) addCol('liability_status');
            return next;
        });
    };

    const handleQuickFilterChange = (event) => {
        const mode = event.target.value;
        setQuickFilterMode(mode);
        
        if (mode !== 'CUSTOM') {
            setIsAllTime(true); 
            if (mode === 'REFUND_PENDING') {
                setFilterLiability([]); 
            } else {
                setFilterLiability(Object.keys(LIABILITY_FILTER_OPTIONS));
            }
        }
    };

    const handleSelectRow = (booking) => {
        setSelectedBookings(prev => {
            const exists = prev.find(b => b.booking_code === booking.booking_code);
            if (exists) {
                return prev.filter(b => b.booking_code !== booking.booking_code);
            } else {
                return [...prev, booking];
            }
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedBookings(filteredList);
        } else {
            setSelectedBookings([]);
        }
    };

    const executeBulkClose = async () => {
        try {
            let successCount = 0;
            const promises = selectedBookings.map(async (bk) => {
                try {
                    await API.updateBooking(bk.booking_code, { booking_status: 'CLOSED' });
                    successCount++;
                } catch (err) {
                    console.error(`Lỗi update ${bk.booking_code}`, err);
                }
            });

            await Promise.all(promises);

            if (successCount > 0) {
                showToast(`Đã hoàn thành thành công ${successCount}/${selectedBookings.length} booking!`, "success");
                setSelectedBookings([]);
                loadData();
            } else {
                showToast("Không thể hoàn thành các booking đã chọn.", "error");
            }
        } catch (error) {
            showToast("Có lỗi xảy ra trong quá trình xử lý.", "error");
        } finally {
            setConfirmModal({ ...confirmModal, open: false });
        }
    };

    const handleOpenBulkClose = () => {
        setConfirmModal({
            open: true,
            title: "Xác nhận Hoàn thành gộp",
            content: `Bạn có chắc chắn muốn chuyển ${selectedBookings.length} booking đã chọn sang trạng thái HOÀN THÀNH (CLOSED)?`,
            onConfirm: executeBulkClose, // Truyền hàm thực thi vào đây
            confirmColor: 'success' // Màu xanh lá cho nút xác nhận
        });
    };

    useEffect(() => { syncToUrl(); }, [sortOrder]);
    useEffect(() => {
        if (quickFilterMode === 'CUSTOM') return;
        if (quickFilterMode === 'READY_TO_CLOSE') {
            setVisibleColumns(prev => {
                let next = [...prev, 'profit_margin', 'payment_status', 'operation_status', 'liability_status'];
                return [...new Set(next)].filter(c => c !== 'total_remaining_liability');
            });
        } else if (quickFilterMode === 'DEBT_CUSTOMER') {
            setVisibleColumns(prev => [...new Set([...prev, 'payment_status'])]);
        } else if (quickFilterMode === 'REFUND_PENDING') {
            const hiddenCols = ['total_price', 'profit', 'profit_margin', 'total_liability', 'total_remaining_liability', 'liability_status','operation_status'];
            const showCols = ['refund_amount', 'refund_account_info', 'booking_status'];
            setVisibleColumns(prev => {
                const next = prev.filter(c => !hiddenCols.includes(c)); // Bỏ cột tài chính
                return [...new Set([...next, ...showCols])]; // Thêm cột hoàn tiền
            });
        } else if (quickFilterMode === 'COLLECTION_PENDING') {
            setVisibleColumns(prev => [...new Set([...prev, 'total_price', 'payment_status', 'sales_owner'])]);
        }
        if (searchParams.get('mode') !== quickFilterMode) syncToUrl();
    }, [quickFilterMode]);

    const CHECKBOX_COLUMN = {
        field: 'checkbox',
        label: (
            <Checkbox
                color="primary"
                indeterminate={selectedBookings.length > 0 && selectedBookings.length < filteredList.length}
                checked={filteredList.length > 0 && selectedBookings.length === filteredList.length}
                onChange={handleSelectAll}
                size="small"
                sx={{p: 0}}
            />
        ),
        minWidth: 40,
        align: 'center'
    };

    const visibleDataColumns = ALL_COLUMNS_DEF.filter(col => visibleColumns.includes(col.field));
    const finalColumns = [CHECKBOX_COLUMN, ...visibleDataColumns, ACTION_COLUMN];

    const renderCountValue = (selected, allKeys, label) => {
        if (selected.length === 0) return `Chọn ${label}`;
        if (selected.length === allKeys.length) return `Tất cả (${selected.length})`;
        return `${selected.length} đã chọn`;
    };

    const getReportColumns = () => {
        const COL_DEFS = {
            'booking_code': { header: 'Mã BK', width: '10%', render: r => r.booking_code },
            'source': { header: 'Nguồn', width: '8%', render: r => r.source || 'Direct' },
            'booking_type': { header: 'Loại BK', width: '10%', render: r => (r.booking_type === 'INTERMEDIARY' ? 'Trung gian' : 'Trực tiếp ĐH') },
            'intermediary_note': { header: 'Thỏa thuận', width: '15%', render: r => r.intermediary_note || '' },
            'customer': { header: 'Khách hàng', width: '15%', render: r => r.customer_detail?.name || 'Khách lẻ' },
            'tour_name': { header: 'Sản phẩm/Tour', width: '18%', render: r => {
                if (r.tour) {
                    const tInfo = tours.find(t => t.tour_code === r.tour);
                    return tInfo ? tInfo.base_tour_name : r.tour_name;
                }
                return BOOKING_CATEGORY_OPTIONS[r.booking_category] || r.booking_category || 'Dịch vụ';
            }},
            'start_date': { header: 'Ngày đi', width: '10%', render: r => formatDateDisplay(r.start_date) },
            'end_date': { header: 'Ngày về', width: '10%', render: r => formatDateDisplay(r.end_date) },
            'sales_owner': { header: 'Sales', width: '10%', render: r => r.sale_owner_name },
            'total_price': { header: 'Tổng thu', width: '10%', render: r => formatCurrency(calculateFinancials(r).revenue) },
            'total_liability': { header: 'Tổng CN', width: '10%', render: r => formatCurrency(calculateFinancials(r).totalLiability) },
            'total_remaining_liability': { header: 'CN Còn', width: '10%', render: r => formatCurrency(calculateFinancials(r).remainingLiability) },
            'profit': { header: 'Lợi nhuận', width: '10%', render: r => formatCurrency(calculateFinancials(r).profit) },
            'profit_margin': { header: '% LN', width: '7%', render: r => `${calculateFinancials(r).margin.toFixed(1)}%` },
            'payment_status': { header: 'Thu khách', width: '10%', render: r => r.payment_status === 'PAID' ? 'Đã thu' : 'Chưa thu' },
            'liability_status': { header: 'Công nợ', width: '10%', render: r => LIABILITY_STATUS_LABELS[r.liability_status] || 'Chưa TT' },
            'booking_status': { header: 'Trạng thái', width: '10%', render: r => BOOKING_STATUS_LABELS[r.booking_status] },
            'operation_status': { header: 'Điều hành', width: '10%', render: r => OPS_STATUS_LABELS[r.operation_status || 'OPEN'] },
            'refund_amount': { header: 'Hoàn tiền', width: '10%', render: r => formatCurrency(calculateFinancials(r).refund_amount) },
            'refund_account_info': { header: 'TK Hoàn', width: '15%', render: r => calculateFinancials(r).refund_account_info || '' },
        };

        return ALL_COLUMNS_DEF
            .filter(col => visibleColumns.includes(col.field)) 
            .map(col => ({ ...COL_DEFS[col.field], accessor: col.field }))
            .filter(Boolean);
    };

    return (
        <Paper sx={{ pt: 2, pb: 2, px: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ mb: 3 }}>
                {/* DÒNG 1 */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', mb: 0 }}>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Bộ lọc nhanh" />
                        <FormControl size="small" fullWidth>
                            <Select value={quickFilterMode} onChange={handleQuickFilterChange} sx={{ bgcolor: 'white' }}>
                                <MenuItem value="CUSTOM">-- Tất cả --</MenuItem>
                                <MenuItem value="REFUND_PENDING" sx={{ color: '#d32f2f'}}>BK cần Hoàn tiền</MenuItem>
                                <MenuItem value="READY_TO_CLOSE">Đủ ĐK Hoàn thành</MenuItem>
                                <MenuItem value="DEBT_CUSTOMER">Khách chưa TT</MenuItem>
                                <MenuItem value="COLLECTION_PENDING">Thu hộ chưa quyết toán</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Loại Booking" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={filterBookingType} onChange={(e) => setFilterBookingType(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => renderCountValue(selected, Object.keys(BOOKING_TYPE_OPTIONS), 'Loại BK')} sx={{ bgcolor: 'white' }}>
                                {Object.keys(BOOKING_TYPE_OPTIONS).map((key) => (<MenuItem key={key} value={key}><Checkbox checked={filterBookingType.indexOf(key) > -1} size="small" /><ListItemText primary={BOOKING_TYPE_OPTIONS[key]} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Nhóm Dịch vụ (Category)" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={filterCategory} onChange={(e) => setFilterCategory(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => renderCountValue(selected, Object.keys(BOOKING_CATEGORY_OPTIONS), 'Dịch vụ')} sx={{ bgcolor: 'white' }}>
                                {Object.keys(BOOKING_CATEGORY_OPTIONS).map((key) => (<MenuItem key={key} value={key}><Checkbox checked={filterCategory.indexOf(key) > -1} size="small" /><ListItemText primary={BOOKING_CATEGORY_OPTIONS[key]} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Nguồn Booking" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={filterSource} onChange={(e) => setFilterSource(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => renderCountValue(selected, SOURCE_OPTIONS, 'Nguồn')} sx={{ bgcolor: 'white' }}>
                                {SOURCE_OPTIONS.map((key) => (<MenuItem key={key} value={key}><Checkbox checked={filterSource.indexOf(key) > -1} size="small" /><ListItemText primary={key} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ width: '180px' }}>
                        <CustomLabel title="NV Phụ trách" />
                        <Autocomplete
                            size="small" fullWidth sx={{ bgcolor: 'white' }}
                            options={salesOwners}
                            getOptionLabel={(opt) => `${opt.last_name || ''} ${opt.first_name || ''}`.trim() || opt.id || 'Tất cả'}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={filterSaleOwner} onChange={(e, val) => setFilterSaleOwner(val)}
                            renderInput={(params) => <TextField {...params} placeholder="Chọn Sales..." />}
                        />
                    </Box>
                    <Box>
                        <Tooltip title={`Sắp xếp ngày đi: ${sortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}`}>
                            <IconButton onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} sx={{ border: '1px solid #ced4da', borderRadius: 1, height: '40px', color: '#007373', marginTop: '26px' }}>
                                <SortIcon sx={{ transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box>
                        <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#007373', height: '40px', px: 3, fontWeight: 'bold', borderRadius: 2, marginTop: '26px'}}>Áp dụng</Button>
                    </Box>
                </Box>

                {/* DÒNG 2 */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', mb: 0 }}>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Lọc theo Nhóm Tour" />
                        <Autocomplete
                            size="small" fullWidth sx={{ bgcolor: 'white' }}
                            options={getTourFilterOptions()} 
                            getOptionLabel={(opt) => opt.label}
                            value={filterTour} onChange={(e, val) => setFilterTour(val)}
                            renderInput={(params) => <TextField {...params} placeholder="Tất cả (Hoặc chọn tour)" />}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '40px', px: 1, bgcolor: 'white', mt: '24px' }}>
                        <FormControlLabel
                            control={<Checkbox checked={isAllTime} onChange={(e) => setIsAllTime(e.target.checked)} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }} />}
                            label={<Typography variant="body2" fontWeight="bold">Toàn TG</Typography>} sx={{ mr: 0 }}
                        />
                    </Box>
                    <Box sx={{ width: '150px' }}>
                        <CustomLabel title="Từ ngày" />
                        <CustomDatePicker value={filterStartDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterStartDate(e.target.value)} />
                    </Box>
                    <Box sx={{ width: '150px' }}>
                        <CustomLabel title="Đến ngày" />
                        <CustomDatePicker value={filterEndDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterEndDate(e.target.value)} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Ẩn/ Hiện cột" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={visibleColumns} onChange={(e) => setVisibleColumns(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => `${selected.length} cột hiển thị`} MenuProps={{ PaperProps: { style: { width: 600, maxHeight: 400 } }, MenuListProps: { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' } } }} sx={{ bgcolor: 'white' }}>
                                {ALL_COLUMNS_DEF.map((col) => (<MenuItem key={col.field} value={col.field} style={{ padding: '4px 10px' }}><Checkbox checked={visibleColumns.indexOf(col.field) > -1} size="small" style={{ padding: 4 }}/><ListItemText primary={col.label || col.field} primaryTypographyProps={{ variant: 'caption' }} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* DÒNG 3 */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Nhà cung cấp (NCC)" />
                        <Autocomplete
                            size="small" fullWidth sx={{ bgcolor: 'white' }}
                            options={suppliers}
                            getOptionLabel={(opt) => opt.name || 'Tất cả NCC'}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={filterSupplier} onChange={(e, val) => setFilterSupplier(val)}
                            renderInput={(params) => <TextField {...params} placeholder="Tất cả đối tác..." />}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Trạng thái Booking" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={filterBookingStatus} onChange={(e) => setFilterBookingStatus(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => renderCountValue(selected, Object.keys(BOOKING_STATUS_LABELS), 'TT Booking')} sx={{ bgcolor: 'white' }}>
                                {Object.keys(BOOKING_STATUS_LABELS).map((key) => (<MenuItem key={key} value={key}><Checkbox checked={filterBookingStatus.indexOf(key) > -1} size="small" /><ListItemText primary={BOOKING_STATUS_LABELS[key]} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Trạng thái Điều hành" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={filterOpsStatus} onChange={(e) => setFilterOpsStatus(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => renderCountValue(selected, Object.keys(OPS_STATUS_LABELS), 'TT Điều hành')} sx={{ bgcolor: 'white' }}>
                                {Object.keys(OPS_STATUS_LABELS).map((key) => (<MenuItem key={key} value={key}><Checkbox checked={filterOpsStatus.indexOf(key) > -1} size="small" /><ListItemText primary={OPS_STATUS_LABELS[key]} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Trạng thái Công nợ" />
                        <FormControl size="small" fullWidth>
                            <Select multiple value={filterLiability} onChange={(e) => setFilterLiability(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput />} renderValue={(selected) => renderCountValue(selected, Object.keys(LIABILITY_FILTER_OPTIONS), 'Công nợ')} sx={{ bgcolor: 'white' }}>
                                {Object.keys(LIABILITY_FILTER_OPTIONS).map((key) => (<MenuItem key={key} value={key}><Checkbox checked={filterLiability.indexOf(key) > -1} size="small" /><ListItemText primary={LIABILITY_FILTER_OPTIONS[key]} /></MenuItem>))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </Box>

            {selectedBookings.length > 0 && (
                <Box sx={{ 
                    mb: 2, p: 1, px: 2, 
                    bgcolor: '#e8f5e9', 
                    border: '1px solid #c8e6c9', 
                    borderRadius: 2,
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    animation: 'fadeIn 0.3s'
                }}>
                    <Box>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                            Đã chọn {selectedBookings.length} booking
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '0.8rem' }}>
                            Tổng giá trị: {formatCurrency(totalSelectedValue)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        
                        {quickFilterMode === 'READY_TO_CLOSE' ? (
                            <Button 
                                variant="contained" 
                                color="success" 
                                size="small" 
                                startIcon={<CheckCircleIcon />} 
                                onClick={handleOpenBulkClose}
                                sx={{ fontWeight: 'bold', bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                            >
                                Hoàn thành gộp
                            </Button>
                        ) : (
                            <Button 
                                variant="contained" 
                                color="info" 
                                size="small" 
                                startIcon={<PaymentIcon />} 
                                onClick={() => setBulkPayOpen(true)}
                                disabled={!bulkPayStatus.isSameSource || bulkPayStatus.hasPaidItems}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {bulkPayStatus.hasPaidItems ? "Đã có BK Đã TT" : (bulkPayStatus.isSameSource ? "Thanh toán BK gộp" : "Khác nguồn BK")}
                            </Button>
                        )}

                        {quickFilterMode === 'REFUND_PENDING' && (
                            <Button 
                                variant="contained" 
                                color="warning" 
                                size="small" 
                                startIcon={<ContentCopyIcon />}
                                onClick={() => setRefundDialogOpen(true)} 
                                sx={{ fontWeight: 'bold' }}
                            >
                                Tạo thông tin hoàn tiền
                            </Button>
                        )}
                    </Box>
                </Box>
            )}

            <DataTable
                maxHeight="65vh"
                columns={finalColumns}
                data={filteredList}
                summaryData={summaryData}
                renderRow={(row, index) => {
                    const { revenue, totalLiability, remainingLiability, profit, margin, refund_amount, refund_account_info } = calculateFinancials(row);
                    const tourInfo = tours.find(t => t.tour_code === row.tour);
                    const isSelected = selectedBookings.some(b => b.booking_code === row.booking_code);
                    
                    let tourDisplayName = '';
                    if (row.tour) {
                        tourDisplayName = tourInfo ? tourInfo.base_tour_name : row.tour_name;
                    } else {
                        tourDisplayName = BOOKING_CATEGORY_OPTIONS[row.booking_category] || row.booking_category || 'Dịch vụ';
                    }

                    return (
                    <TableRow key={row.booking_code} hover selected={isSelected}>
                        <TableCell padding="checkbox"><Checkbox checked={isSelected} onChange={() => handleSelectRow(row)} /></TableCell>
                        {visibleColumns.includes('booking_code') && <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.booking_code}</TableCell>}
                        {visibleColumns.includes('source') && <TableCell align="center">{row.source || 'Direct'}</TableCell>}
                        {visibleColumns.includes('booking_type') && <TableCell align="center"><Chip label={(row.booking_type === 'INTERMEDIARY') ? 'Trung gian' : 'Trực tiếp'} color={(row.booking_type === 'INTERMEDIARY') ? 'warning' : 'default'} size="small" variant="outlined" sx={{ fontWeight: 'bold' }}/></TableCell>}
                        {visibleColumns.includes('intermediary_note') && <TableCell><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#d84315' }}>{row.intermediary_note || '---'}</Typography></TableCell>}
                        {visibleColumns.includes('customer') && <TableCell><Typography variant="body2" fontWeight="bold">{row.customer_detail ? row.customer_detail.name : 'Khách lẻ'}</Typography><Typography variant="caption" color="textSecondary">{row.customer_detail?.phone}</Typography></TableCell>}
                        {visibleColumns.includes('tour_name') && <TableCell>{tourDisplayName}</TableCell>}
                        {visibleColumns.includes('start_date') && <TableCell>{formatDateDisplay(row.start_date)}</TableCell>}
                        {visibleColumns.includes('end_date') && <TableCell>{formatDateDisplay(row.end_date)}</TableCell>}
                        {visibleColumns.includes('sales_owner') && <TableCell>{row.sale_owner_name}</TableCell>}
                        {visibleColumns.includes('total_price') && <TableCell align="right">{formatCurrency(revenue)}</TableCell>}
                        {visibleColumns.includes('total_liability') && <TableCell align="right" sx={{color: '#d32f2f'}}>{formatCurrency(totalLiability)}</TableCell>}
                        {visibleColumns.includes('total_remaining_liability') && <TableCell align="right" sx={{color: remainingLiability > 0 ? 'error.main' : 'inherit', fontWeight: remainingLiability > 0 ? 'bold' : 'normal'}}>{formatCurrency(remainingLiability)}</TableCell>}
                        {visibleColumns.includes('profit') && <TableCell align="right" sx={{fontWeight:'bold', color: '#2e7d32'}}>{formatCurrency(profit)}</TableCell>}
                        {visibleColumns.includes('profit_margin') && <TableCell align="center">{margin.toFixed(1)}%</TableCell>}
                        {visibleColumns.includes('payment_status') && <TableCell align="center"><Chip label={row.payment_status === 'PAID' ? 'Đã thu' : 'Chưa thu'} color={row.payment_status === 'PAID' ? 'success' : 'warning'} size="small" variant={row.payment_status === 'PAID' ? 'filled' : 'outlined'} /></TableCell>}
                        {visibleColumns.includes('liability_status') && <TableCell align="center"><Chip label={LIABILITY_STATUS_LABELS[row.liability_status] || 'Chưa TT'} color={LIABILITY_STATUS_COLORS[row.liability_status] || 'error'} size="small" variant={row.liability_status === 'PAID' ? 'filled' : 'outlined'} /></TableCell>}
                        {visibleColumns.includes('booking_status') && <TableCell><Chip label={BOOKING_STATUS_LABELS[row.booking_status]} color={BOOKING_STATUS_COLORS[row.booking_status]} size="small" variant="outlined" /></TableCell>}
                        {visibleColumns.includes('operation_status') && <TableCell><Chip label={OPS_STATUS_LABELS[row.operation_status || 'OPEN']} color={OPS_STATUS_COLORS[row.operation_status || 'OPEN']} size="small" /></TableCell>}
                        {visibleColumns.includes('refund_amount') && <TableCell align="right" sx={{ color: 'red', fontWeight: 'bold' }}>{formatCurrency(refund_amount)}</TableCell>}
                        {visibleColumns.includes('refund_account_info') && <TableCell sx={{ maxWidth: 200 }}><Typography variant="caption" sx={{whiteSpace: 'pre-wrap'}}>{refund_account_info || '---'}</Typography></TableCell>}
                        <TableCell align="center">
                            <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem', py: 0.5, textTransform: 'none', bgcolor: 'white' }} onClick={() => navigate('/form', { state: { bookingCodeToLoad: row.booking_code } })}>Xem</Button>
                        </TableCell>
                    </TableRow>
                )}}
            />
            <RefundInfoDialog 
                open={refundDialogOpen}
                onClose={() => setRefundDialogOpen(false)}
                selectedBookings={selectedBookings}
                showToast={showToast}
            />
            <BulkPaymentDialog 
                open={bulkPayOpen}
                onClose={() => setBulkPayOpen(false)}
                type="BOOKING"
                items={selectedBookings.map(b => {
                    const total = parseFloat(b.total_price || 0);
                    const received = parseFloat(b.amount_received || 0);
                    const remain = total - received;

                    return {
                        id: b.booking_code,
                        amount: remain > 0 ? remain : 0,
                        customer_name: b.customer_detail?.name || 'Khách hàng',
                        sender_info: b.source || 'Direct',
                        source: b.source
                    };
                })}
                showToast={showToast}
                onSuccess={() => {
                    setSelectedBookings([]);
                    loadData();
                }}
            />
            <ConfirmDialog 
                open={confirmModal.open}
                title={confirmModal.title}
                content={confirmModal.content}
                onClose={() => setConfirmModal({ ...confirmModal, open: false })}
                onConfirm={confirmModal.onConfirm}
                confirmColor={confirmModal.confirmColor || 'primary'}
                confirmText="Xác nhận"
                cancelText="Hủy bỏ"
            />
            {(() => {
                const reportData = selectedBookings.length > 0 ? selectedBookings : filteredList;
                const reportSummary = reportData.reduce((acc, bk) => {
                    const { revenue, totalLiability, remainingLiability, profit, refund_amount } = calculateFinancials(bk);
                    acc.totalRevenue += revenue;
                    acc.totalLiability += totalLiability;
                    acc.totalRemainingLiability += remainingLiability;
                    acc.totalProfit += profit;
                    acc.totalRefund += refund_amount;
                    return acc;
                }, { totalRevenue: 0, totalLiability: 0, totalRemainingLiability: 0, totalProfit: 0, totalRefund: 0 });

                const summaryRowData = {
                    booking_code: 'TỔNG CỘNG:',
                    total_price: formatCurrency(reportSummary.totalRevenue),
                    total_liability: formatCurrency(reportSummary.totalLiability),
                    total_remaining_liability: formatCurrency(reportSummary.totalRemainingLiability),
                    profit: formatCurrency(reportSummary.totalProfit),
                    refund_amount: formatCurrency(reportSummary.totalRefund),
                };

                return (
                    <ReportExportDialog 
                        open={reportOpen}
                        onClose={() => setReportOpen(false)}
                        type="BOOKING_REVENUE" 
                        data={reportData}
                        dateRange={{ start: activeFilters.startDate, end: activeFilters.endDate }}
                        customColumns={getReportColumns()}
                        summaryRow={summaryRowData}
                        fileNamePrefix="BaoCao_Booking"
                        customTitle="BÁO CÁO TỔNG QUAN BOOKING"
                    />
                );
            })()}
            <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ mt: 8 }}><Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert></Snackbar>
        </Paper>
    );
});

export default BookingListTab;